import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
	LoginForm,
	MfaChallengeForm,
	useAuth,
} from '@ekhodealer/ekho-common/auth-client';
import { Callout } from '@ekhodealer/ekho-common/rev';

import { useMintlifyDocsToken } from '../hooks/useMintlifyDocsToken';

/**
 * Main content component for Mintlify login flow
 * Handles authentication state and renders appropriate form (login or MFA)
 */
export function MintlifyLogin() {
	const { isAuthenticated, accessToken, mfaPending } = useAuth();
	const [searchParams] = useSearchParams();
	const redirect = searchParams.get('redirect');
	const { fetchAndRedirect, isFetching, error, clearError, hasAttempted } =
		useMintlifyDocsToken();

	// Track if we were on MFA screen - this stays true until we navigate away
	// We use state (not just ref) so component re-renders when it changes
	const wasOnMfaScreenRef = useRef(false);

	// Track when we're on MFA screen - set to true when MFA starts
	useEffect(() => {
		if (mfaPending) {
			wasOnMfaScreenRef.current = true;
		}
	}, [mfaPending]);

	// Determine if we should show the MFA form:
	// - During MFA challenge (mfaPending)
	// - After MFA completes but before navigation (wasOnMfaScreenRef + authenticated)
	// The key insight: if user was on MFA screen and is now authenticated,
	// keep showing MFA screen until redirect happens (which unmounts the component)
	const shouldShowMfaForm =
		mfaPending || (wasOnMfaScreenRef.current && isAuthenticated);

	// isProcessing should be true when MFA completed and we're fetching OR about to fetch
	// This includes the brief moment after MFA completes but before useEffect sets isFetching
	const isPostMfaProcessing =
		wasOnMfaScreenRef.current && !mfaPending && isAuthenticated;

	// After login, fetch docs token and redirect
	useEffect(() => {
		if (isAuthenticated && accessToken && !isFetching && !hasAttempted) {
			// User is authenticated - proceed with token fetch
			fetchAndRedirect(redirect);
		}
	}, [
		isAuthenticated,
		accessToken,
		redirect,
		isFetching,
		hasAttempted,
		fetchAndRedirect,
	]);

	const handleMfaCancel = () => {
		// If user cancels MFA, they can try logging in again
		// The LoginForm will be shown again
		// Reset the MFA screen ref so we show login form, not MFA form
		wasOnMfaScreenRef.current = false;
		clearError();
	};

	// Show MFA challenge form if MFA is required OR if we just completed MFA (before redirect)
	// isProcessing is true immediately after MFA completes, not just when isFetching is true
	if (shouldShowMfaForm) {
		return (
			<>
				<MfaChallengeForm
					onCancel={handleMfaCancel}
					isProcessing={isPostMfaProcessing || isFetching}
				/>
				{error && <Callout variant='error' message={error} />}
			</>
		);
	}

	// Show login form in all other cases:
	// - Not authenticated
	// - Authenticated while fetching token (shows loading button)
	return (
		<LoginForm
			details={['Access Ekho documentation and resources.']}
			error={error}
			hideForgotPassword={true}
			isProcessing={isFetching}
		/>
	);
}
